import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronUp } from "lucide-react";
import { api } from "@/lib/api";

const FALLBACK = [
  { slug: "privacy-policy", title: "Privacy Policy" },
  { slug: "terms-and-conditions", title: "Terms & Conditions" },
  { slug: "refund-cancellation-policy", title: "Cancellation & Refund Policy" },
  { slug: "service-restrictions", title: "Service Restrictions" },
  { slug: "payment-policy", title: "Payment Policy" },
];

/**
 * Policy links for the very end of the page: ONE line with the main policies, and a "More" menu for the rest.
 * The list comes from Admin > Support & Data > Policies. `visible` = how many stay on the line.
 */
export default function PolicyLinks({ className = "", visible }) {
  const keep = visible ?? (typeof window !== "undefined" && window.innerWidth < 560 ? 2 : 3);
  const [rows, setRows] = useState(FALLBACK);
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    api.get("/policies").then((r) => { if (Array.isArray(r.data) && r.data.length) setRows(r.data); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", away); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  const shown = rows.slice(0, keep);
  const rest = rows.slice(keep);
  return (
    <nav aria-label="Policies" data-testid="policy-links" className={`pl-line ${className}`}>
      {shown.map((p) => (
        <Link key={p.slug} to={`/policy/${p.slug}`} className="pl-link" data-testid={`policy-link-${p.slug}`}>{p.title}</Link>
      ))}
      <span className="pl-more" ref={box}>
        <button type="button" className="pl-morebtn" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((v) => !v)} data-testid="policy-more">
          More<ChevronUp aria-hidden="true" className={open ? "is-open" : ""} />
        </button>
        {open && (
          <div className="pl-menu" role="menu">
            {rest.map((p) => (
              <Link key={p.slug} to={`/policy/${p.slug}`} role="menuitem" onClick={() => setOpen(false)} data-testid={`policy-link-${p.slug}`}>{p.title}</Link>
            ))}
            <Link to="/policies" role="menuitem" className="pl-all" onClick={() => setOpen(false)} data-testid="policy-link-all">All policies</Link>
          </div>
        )}
      </span>
    </nav>
  );
}
